/**
 * Light, dark, or whatever the operating system says — the same three choices the public
 * website offers, kept under the same storage key.
 *
 * `index.html` applies the stored choice before the bundle loads, so a dark-mode visitor never
 * sees a white frame. This module takes over from there: it is the only writer of
 * `data-theme` (the resolved `light` / `dark` the stylesheet keys on) and `data-theme-pref`
 * (the choice itself) after the first paint, and it re-resolves `system` when the operating
 * system flips or another tab changes the choice.
 */
import { useSyncExternalStore } from 'react'
import { appKey } from '@layerit/brand'

export type Theme = 'system' | 'light' | 'dark'
export type ResolvedTheme = 'light' | 'dark'

export const THEME_STORAGE_KEY = appKey('theme')
const THEME_COLOR: Record<ResolvedTheme, string> = { light: '#ffffff', dark: '#141210' }
const CHANGE_EVENT = appKey('theme-change')

export function isTheme(value: unknown): value is Theme {
	return value === 'system' || value === 'light' || value === 'dark'
}

function darkQuery(): MediaQueryList | null {
	return typeof window === 'undefined' || typeof window.matchMedia !== 'function'
		? null
		: window.matchMedia('(prefers-color-scheme: dark)')
}

function storedTheme(): Theme {
	try {
		const stored = window.localStorage.getItem(THEME_STORAGE_KEY)
		return stored === 'light' || stored === 'dark' ? stored : 'system'
	} catch {
		return 'system'
	}
}

export function resolveTheme(theme: Theme, prefersDark: boolean): ResolvedTheme {
	return theme === 'dark' || (theme === 'system' && prefersDark) ? 'dark' : 'light'
}

function apply(theme: Theme): void {
	const resolved = resolveTheme(theme, darkQuery()?.matches ?? false)
	const root = document.documentElement
	root.dataset.themePref = theme
	root.dataset.theme = resolved
	document.querySelector('meta[name="theme-color"]')?.setAttribute('content', THEME_COLOR[resolved])
	window.dispatchEvent(new Event(CHANGE_EVENT))
}

export function setTheme(theme: Theme): void {
	try {
		if (theme === 'system') window.localStorage.removeItem(THEME_STORAGE_KEY)
		else window.localStorage.setItem(THEME_STORAGE_KEY, theme)
	} catch {}
	apply(theme)
}

function currentTheme(): Theme {
	const pref = document.documentElement.dataset.themePref
	return isTheme(pref) ? pref : storedTheme()
}

function subscribe(onChange: () => void): () => void {
	const query = darkQuery()
	const onSystem = () => {
		if (currentTheme() === 'system') apply('system')
	}
	const onStorage = (event: StorageEvent) => {
		if (event.key === THEME_STORAGE_KEY) apply(isTheme(event.newValue) ? event.newValue : 'system')
	}
	window.addEventListener(CHANGE_EVENT, onChange)
	window.addEventListener('storage', onStorage)
	query?.addEventListener('change', onSystem)
	return () => {
		window.removeEventListener(CHANGE_EVENT, onChange)
		window.removeEventListener('storage', onStorage)
		query?.removeEventListener('change', onSystem)
	}
}

function snapshot(): string {
	const root = document.documentElement
	return `${currentTheme()}:${root.dataset.theme === 'dark' ? 'dark' : 'light'}`
}

export function useTheme(): { theme: Theme; resolved: ResolvedTheme; setTheme: (theme: Theme) => void } {
	const [theme, resolved] = useSyncExternalStore(subscribe, snapshot, () => 'system:light').split(':')
	return {
		theme: isTheme(theme) ? theme : 'system',
		resolved: resolved === 'dark' ? 'dark' : 'light',
		setTheme,
	}
}
